import sessionStore from '@/session/sessionStore'
import moment from 'moment'
import { store } from '@/store/store'
import { authService } from '@/api/services/auth/auth.service'
import { GlobalExternalAuthenticationResponse, UserTokenResponse } from '@/api/services/auth/auth.dto'
import { commonParameterService } from '@/api/services/common/parameter/common-parameter.service'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { StoreUtils } from '@/shared/util/store-utils'
import { Nullable, StrictNullable } from '@/shared/types/app.types'

const TWO_MINUTES_IN_SECONDS = 120

class Auth {
    private refreshTokenTimeout: number | undefined
    private userDowntimeTimeout: number | undefined

    get token(): string {
        return sessionStorage.token
    }
    set token(token: string) {
        sessionStorage.token = token
    }

    isPasswordSingleUse(): boolean {
        return JSON.parse(sessionStorage.passwordSingleUse)
    }

    getCountdownToCloseSessionInSeconds(): number {
        return +sessionStorage.countdownToCloseSessionInSeconds
    }

    async logIn(username: string, password: string): Promise<void> {
        if (sessionStorage.getItem('token')) {
            this.logOut()
        }
        const userToken = await authService.login({ username, password })

        await this.updateSessionStorage(userToken, true)
        this.resetRefreshTokenTimeout()
        this.resetUserDowntimeTimeout()
    }

    async globalExternalAuthentication(): Promise<Nullable<boolean>> {
        const globalExternalAuthentication = await PromiseUtils.toCallSecure(
            authService.isGlobalExternalAuthentication(sessionStore.getLocale())
        )
        return globalExternalAuthentication?.globalExternalAuthentication
    }

    async canChangePassword(personnelUserDefaultLabId: StrictNullable<number> = null): Promise<boolean> {
        const globalExternalAuthentication = await this.globalExternalAuthentication()
        if (globalExternalAuthentication) {
            return false
        }

        const userDefaultLabId = personnelUserDefaultLabId ?? sessionStore.getUserDefaultLabId()
        if (userDefaultLabId) {
            const useLdap =
                await PromiseUtils.toCallSecure(
                    commonParameterService.getParameter(
                        userDefaultLabId,
                        'configuration.parameter.laboratory.ldap.use'))
            if (useLdap) {
                return false
            }
        }

        return true
    }

    async changePassword(oldPassword: string, newPassword: string): Promise<[any, void]> {
        const [err, data] = await authService.changePassword({ oldPassword, newPassword })
            .then(data2 => { return [null, data2] })
            .catch(err2 => { return [err2] })
        return [err, data]
    }

    async recoveryPassword(username: string): Promise<[any, void]> {
        const [err, data] = await authService.recoveryPassword(username)
            .then(data2 => { return [null, data2] })
            .catch(err2 => { return [err2] })
        return [err, data]
    }

    async isGlobalExternalAuthentication(locale: string): Promise<[any, GlobalExternalAuthenticationResponse]> {
        const [err, data] = await authService.isGlobalExternalAuthentication(locale)
            .then(data2 => { return [null, data2] })
            .catch(err2 => { return [err2] })
        return [err, data]
    }

    logOut(): void {
        StoreUtils.updateUserLoggedIn(false)
    }

    clearSession(): void {
        this._cancelRefreshTokenTimeout()
        this._cancelUserDowntimeTimeout()

        sessionStorage.removeItem('labId')
        Auth._clearTokenData()
    }

    private static _clearTokenData(): void {
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('expires')
        sessionStorage.removeItem('passwordSingleUse')
        sessionStorage.removeItem('userDowntimeInSeconds')
        sessionStorage.removeItem('countdownToCloseSessionInSeconds')
        sessionStorage.removeItem('downtimeDateLimit')
        sessionStore.reset()
    }

    private static async _updateSession(updateUserInformation: boolean): Promise<void> {
        sessionStore.setToken(sessionStorage.token)
        sessionStore.setExpires(sessionStorage.expires)
        sessionStore.setPasswordSingleUse(sessionStorage.passwordSingleUse)
        sessionStore.setUserDowntimeInSeconds(sessionStorage.userDowntimeInSeconds)
        sessionStore.setCountdownToCloseSessionInSeconds(sessionStorage.countdownToCloseSessionInSeconds)

        if (updateUserInformation) {
            try {
                await sessionStore.update()
            } catch (e) {
                Auth._clearTokenData()
                throw e
            }
        }
    }

    async updateSessionStorage(userToken: UserTokenResponse, updateUserInformation: boolean): Promise<void> {
        sessionStorage.token = userToken.accessToken
        sessionStorage.expires = userToken.expiresIn
        sessionStorage.passwordSingleUse = userToken.passwordSingleUse
        sessionStorage.userDowntimeInSeconds = userToken.userDowntimeInSeconds
        sessionStorage.countdownToCloseSessionInSeconds = userToken.countdownToCloseSessionInSeconds

        StoreUtils.updateUserLoggedIn(true)
        await Auth._updateSession(updateUserInformation)
    }

    private _cancelRefreshTokenTimeout(): void {
        if (this.refreshTokenTimeout) {
            clearTimeout(this.refreshTokenTimeout)
        }
    }

    resetRefreshTokenTimeout(): void {
        this._cancelRefreshTokenTimeout()

        if (store.getters.userLoggedIn) {
            const timeout = (sessionStorage.expires - TWO_MINUTES_IN_SECONDS) * 1000
            this.refreshTokenTimeout = setTimeout(this.refreshToken, timeout)
        }
    }

    private _cancelUserDowntimeTimeout(): void {
        if (this.userDowntimeTimeout) {
            clearTimeout(this.userDowntimeTimeout)
        }
        StoreUtils.updateShowUserDowntimeModal(false)
    }

    resetUserDowntimeTimeout(): void {
        this._cancelUserDowntimeTimeout()

        if (store.getters.userLoggedIn && sessionStorage.userDowntimeInSeconds) {
            sessionStorage.downtimeDateLimit = moment().add(sessionStorage.userDowntimeInSeconds, 'seconds').format()
            const timeout = (sessionStorage.userDowntimeInSeconds - this.getCountdownToCloseSessionInSeconds()) * 1000
            this.userDowntimeTimeout = setTimeout(Auth._showCountdownModal, timeout)
        }
    }

    async refreshToken(updateUserInformation: boolean = false, token?: string) {
        try {
            if (token) {
                sessionStorage.token = token
            }
            const data = await authService.refresh()
            await auth.updateSessionStorage(data, updateUserInformation)
        } catch (e) {
            console.error('Error refreshing token. ¡Forced logout!', e)
            auth.logOut()
        } finally {
            auth.resetRefreshTokenTimeout()
        }
    }

    private static _showCountdownModal(): void {
        if (store.getters.userLoggedIn && !store.getters.showUserDowntimeModal) {
            StoreUtils.updateShowUserDowntimeModal(true)
        }
    }
}

export const auth = new Auth()
